// ============================================================================
// Auth context: Firebase Google SSO → JWT-bridge exchange → Supabase queries.
//
// Sign-in flow:
//   1. Firebase Auth completes Google SSO. Path depends on platform:
//        • Desktop                → signInWithPopup (no navigation)
//        • Mobile / standalone PWA → signInWithRedirect (full-page redirect)
//      iOS Safari and PWA contexts partition sessionStorage between
//      accounts.google.com and our origin, which breaks signInWithPopup's
//      hidden-iframe fallback. signInWithRedirect + browserLocalPersistence
//      (set in firebase.ts) is the supported path on those clients.
//   2. Firebase issues an ID token (1hr TTL by default).
//   3. We POST that token to the `firebase-jwt-bridge` Edge Function.
//   4. The function returns a Supabase JWT signed with the project secret.
//   5. We register the token via setSupabaseToken(); RLS now sees the user.
//   6. We hydrate Employee + UserProfile from the bridge response and from
//      one direct employees query (for fields the bridge doesn't return).
//
// Redirect resolution:
//   On any page load we call getRedirectResult() once. If the user just
//   came back from accounts.google.com, this returns the result and the
//   onAuthStateChanged listener picks up the user from there. If there's
//   no pending redirect, it returns null and we proceed normally. Errors
//   surface via setError() so the sign-in screen can show them.
//
// Token refresh:
//   Firebase ID tokens expire in 1hr. We schedule a re-exchange ~5min before
//   our Supabase JWT expires. getIdToken(true) forces a Firebase refresh so
//   we always start from a fresh source token.
//
// On any 401 from a query, components don't need special handling — the
// proactive refresh covers it. If a 401 still happens (clock skew, cold
// start of the function), users can re-trigger sign-in.
// ============================================================================

import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { firebaseAuth, googleProvider } from './firebase';
import {
  BRIDGE_URL,
  SUPABASE_ANON_KEY,
  setAuthRefreshHandler,
  setSupabaseToken,
  supabase,
} from './supabase';
import type { BridgedUser, Employee, UserProfile } from './types';
import { errorMessage } from './errors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthContextValue {
  user: FirebaseUser | null;
  bridged: BridgedUser | null;
  employee: Employee | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  // Machine-readable code for the most recent bridge failure (e.g.
  // `no_employee`, `inactive_employee`), or null when the failure carried no
  // code / there is no error. Lets the sign-in UI branch on the failure mode.
  errorCode: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Platform detection
// ---------------------------------------------------------------------------

// True when we should use signInWithRedirect instead of signInWithPopup.
// Phones, tablets, and standalone PWA contexts all qualify because:
//   • iOS Safari and Chrome iOS run all browsers on WebKit, which
//     partitions sessionStorage between accounts.google.com and our origin.
//     The popup flow's hidden-iframe completion can't read the
//     pre-redirect state and fails with "missing initial state".
//   • Standalone (added-to-home-screen) PWAs can't open popups at all on
//     iOS — popup() returns null.
//   • Android Chrome handles popups but the redirect path is more reliable
//     under low-memory conditions.
// We err on the side of treating any touch device as mobile-like; the
// redirect flow works fine on desktop too, it's just slightly less smooth.
function isMobileLike(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) return true;
  // Standalone PWA (iOS) — `navigator.standalone` is iOS-specific.
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  // Standalone PWA (Android / others) — display-mode media query.
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  ) {
    return true;
  }
  return false;
}

interface BridgeResponse {
  token: string;
  expiresAt: number; // unix seconds
  user: BridgedUser;
}

// Carries the bridge's machine-readable `code` (e.g. `no_employee`,
// `inactive_employee`, `account_disabled`) alongside the human message so the
// sign-in UI can render distinct copy per failure mode instead of a generic
// error. The bridge already returns these codes — we just stop discarding them.
export class BridgeError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'BridgeError';
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Bridge call
// ---------------------------------------------------------------------------

// Refresh ~5min before the Supabase JWT actually expires, to absorb network
// latency and clock skew without ever hitting the cliff.
const REFRESH_LEAD_SECONDS = 5 * 60;

async function exchangeFirebaseToken(
  firebaseToken: string,
): Promise<BridgeResponse> {
  const res = await fetch(BRIDGE_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${firebaseToken}`,
      // Supabase Edge Functions still require the apikey header even when
      // verify_jwt is false on the function — it identifies the project.
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    let message = `Bridge exchange failed (${res.status})`;
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      if (body?.error) message = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* swallow JSON parse errors — keep the HTTP message */
    }
    throw new BridgeError(code, message);
  }

  return (await res.json()) as BridgeResponse;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [bridged, setBridged] = useState<BridgedUser | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Refs survive re-renders and React Strict-Mode double-invocation. Used to
  // (a) cancel a pending refresh when sign-out happens, and (b) avoid stale
  // state writes after the active Firebase user changes mid-flight.
  const refreshTimer = useRef<number | null>(null);
  const activeFbUid = useRef<string | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearRefreshTimer();
    setSupabaseToken(null);
    setBridged(null);
    setEmployee(null);
    setProfile(null);
  }, [clearRefreshTimer]);

  // ------- Bridge + hydrate ------------------------------------------------

  const runBridge = useCallback(
    async (fbUser: FirebaseUser, forceFreshIdToken = false) => {
      const idToken = await fbUser.getIdToken(forceFreshIdToken);
      const result = await exchangeFirebaseToken(idToken);

      // Bail if the user signed out (or switched) while we were in flight.
      if (activeFbUid.current !== fbUser.uid) return;

      setSupabaseToken(result.token);
      setBridged(result.user);

      // Hydrate the full Employee row — RLS now allows the read because the
      // user is `authenticated` with auth.uid() = user_profiles.id. We use
      // the bridged employee_id rather than email so we don't rely on the
      // employees table having a unique index on email_work.
      const { data: employeeRow, error: empErr } = await supabase
        .from('employees')
        .select(
          'employee_id, employee_name_first, employee_name_last, email_work, email_personal, position_id, role_type, primary_facility_id, employment_status',
        )
        .eq('employee_id', result.user.employee_id)
        .maybeSingle<Employee>();

      if (activeFbUid.current !== fbUser.uid) return;
      if (empErr) {
        // Don't fatal — we still have everything from the bridge response.
        // Log so we notice if RLS is blocking employees reads.
        console.warn('[auth] employees hydrate failed', empErr);
      }
      setEmployee(employeeRow ?? null);

      // Same for user_profiles — useful for created_at/updated_at and any
      // other fields the bridge doesn't surface.
      const { data: profileRow, error: profErr } = await supabase
        .from('user_profiles')
        .select(
          'id, employee_id, role, extra_roles, status, display_name, email, firebase_uid, last_login_at, notes, updated_by, created_at, updated_at',
        )
        .eq('id', result.user.id)
        .maybeSingle<UserProfile>();

      if (activeFbUid.current !== fbUser.uid) return;
      if (profErr) {
        console.warn('[auth] user_profiles hydrate failed', profErr);
      }
      setProfile(profileRow ?? null);

      // Schedule the next exchange.
      const nowMs = Date.now();
      const expMs = result.expiresAt * 1000;
      const delayMs = Math.max(expMs - nowMs - REFRESH_LEAD_SECONDS * 1000, 0);
      clearRefreshTimer();
      refreshTimer.current = window.setTimeout(() => {
        if (activeFbUid.current === fbUser.uid) {
          // Force a fresh Firebase ID token (true) — the cached one may be
          // about to expire too.
          runBridge(fbUser, true).catch((e) => {
            console.error('[auth] proactive refresh failed', e);
            setError(errorMessage(e, 'Token refresh failed.', 'auth'));
          });
        }
      }, delayMs);
    },
    [clearRefreshTimer],
  );

  // ------- Reactive 401 recovery -------------------------------------------
  // The proactive setTimeout refresh dies across laptop sleep / background-tab
  // throttling. supabase.ts calls this handler when a request 401s while a
  // bridged token is set: re-run the bridge once and let it retry (AFB-0029).

  useEffect(() => {
    setAuthRefreshHandler(async () => {
      const fbUser = firebaseAuth.currentUser;
      if (!fbUser || activeFbUid.current !== fbUser.uid) return false;
      try {
        await runBridge(fbUser, true);
        return true;
      } catch (e) {
        console.error('[auth] reactive 401 refresh failed', e);
        return false;
      }
    });
    return () => setAuthRefreshHandler(null);
  }, [runBridge]);

  // ------- Firebase auth state listener -----------------------------------

  useEffect(() => {
    // Resolve any pending redirect FIRST. If the user just came back from
    // accounts.google.com via signInWithRedirect, this completes the flow
    // and Firebase fires onAuthStateChanged with the new user. If there's
    // no pending redirect, this resolves to null and the state listener
    // picks up either a persisted user (browserLocalPersistence) or null.
    //
    // We call getRedirectResult before subscribing because the listener
    // is set up unconditionally below — the resolve order doesn't matter
    // for correctness, only for the order of state transitions the user
    // perceives. Errors here surface via setError() so the sign-in screen
    // can show them; we don't fatal because the user can retry.
    getRedirectResult(firebaseAuth).catch((e) => {
      console.error('[auth] getRedirectResult failed', e);
      setError(
        `Sign-in completion failed: ${errorMessage(e, 'no detail returned', 'auth')}`,
      );
    });

    const unsub = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      activeFbUid.current = fbUser?.uid ?? null;
      setUser(fbUser);
      setError(null);
      setErrorCode(null);

      if (!fbUser) {
        teardown();
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await runBridge(fbUser, false);
      } catch (e) {
        console.error('[auth] sign-in bridge failed', e);
        setError(errorMessage(e, 'Sign-in failed.', 'auth'));
        setErrorCode(e instanceof BridgeError ? e.code : null);
        // On bridge failure, clear any stale Supabase auth state so the
        // user isn't sitting with a broken session.
        teardown();
      } finally {
        // Only flip loading off if this user is still the active one.
        if (activeFbUid.current === (fbUser?.uid ?? null)) {
          setLoading(false);
        }
      }
    });

    return () => {
      unsub();
      clearRefreshTimer();
    };
  }, [runBridge, teardown, clearRefreshTimer]);

  // ------- Public API ------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      bridged,
      employee,
      profile,
      loading,
      error,
      errorCode,
      signIn: async () => {
        // Mobile / standalone PWA: signInWithRedirect. The page navigates
        // to accounts.google.com, the user signs in, the page navigates
        // back. AuthProvider remounts; getRedirectResult() in the effect
        // above completes the flow and onAuthStateChanged picks it up.
        //
        // Desktop: signInWithPopup. No navigation; the bridge runs in
        // onAuthStateChanged once the popup closes.
        if (isMobileLike()) {
          await signInWithRedirect(firebaseAuth, googleProvider);
          // signInWithRedirect navigates the page — execution effectively
          // ends here. The remount handles the result.
          return;
        }
        await signInWithPopup(firebaseAuth, googleProvider);
        // The bridge runs in the onAuthStateChanged handler above.
      },
      signOut: async () => {
        await firebaseSignOut(firebaseAuth);
        // teardown is called in onAuthStateChanged when fbUser becomes null.
      },
    }),
    [user, bridged, employee, profile, loading, error, errorCode],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
